import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { notificationService } from '../services/notificationService';
import { emailSummaryService } from '../services/socialEmailService';

// Maps every DB notification type to a toast visual category
const TOAST_TYPES = {
  // Duel events
  duel_invitation: 'duel_invite',
  duel_accepted: 'duel_invite',
  duel_completed: 'achievement',
  // Friend events
  friend_request: 'friend_request',
  friend_accepted: 'friend_request',
  // Achievement / milestone events (own + friends)
  goal_achievement: 'achievement',
  activity_milestone: 'achievement',
  badge_earned: 'achievement',
  friend_milestone: 'achievement',
  // Squad events
  squad_invitation: 'friend_request',
  squad_member_joined: 'friend_request',
  squad_join_request: 'friend_request',
  // Reminders
  meal_reminder: 'achievement',
  sleep_insight: 'achievement',
  challenge_invitation: 'duel_invite',
};

const buildToastFromNotification = (notification) => {
  const type = TOAST_TYPES?.[notification?.type] || null;
  if (!type) return null;

  const actions = [];

  if (notification?.type === 'duel_invitation') {
    actions?.push(
      { label: 'Ver Duelo', path: '/social-hub' },
      { label: 'Ignorar', dismiss: true }
    );
  } else if (notification?.type === 'duel_accepted') {
    actions?.push(
      { label: 'Ver Duelo', path: '/social-hub' },
      { label: 'Cerrar', dismiss: true }
    );
  } else if (notification?.type === 'duel_completed') {
    actions?.push(
      { label: 'Ver Resultado', path: '/social-hub' }
    );
  } else if (notification?.type === 'friend_request') {
    actions?.push(
      { label: 'Ver Solicitud', path: '/social-hub' },
      { label: 'Ignorar', dismiss: true }
    );
  } else if (notification?.type === 'friend_accepted') {
    actions?.push(
      { label: 'Ver Perfil', path: '/social-hub' }
    );
  } else if (
    notification?.type === 'badge_earned' ||
    notification?.type === 'friend_milestone' ||
    notification?.type === 'goal_achievement' ||
    notification?.type === 'activity_milestone'
  ) {
    actions?.push(
      { label: 'Ver Logro', path: '/notifications-center' }
    );
  } else if (
    notification?.type === 'squad_invitation' ||
    notification?.type === 'squad_join_request'
  ) {
    actions?.push(
      { label: 'Ver Squad', path: '/social-hub' },
      { label: 'Ignorar', dismiss: true }
    );
  } else if (notification?.type === 'challenge_invitation') {
    actions?.push(
      { label: 'Ver Reto', path: '/social-hub' },
      { label: 'Ignorar', dismiss: true }
    );
  }

  // Duration: achievement toasts stay longer
  const isAchievement = type === 'achievement';

  return {
    id: `toast-${notification?.id || Date.now()}-${Math.random()}`,
    notificationId: notification?.id,
    type,
    title: notification?.title || 'Nueva notificación',
    message: notification?.message || '',
    actions,
    duration: isAchievement ? 8000 : 6000,
    persistent: false,
  };
};

export const useToastNotifications = (userId) => {
  const [toasts, setToasts] = useState([]);
  const navigate = useNavigate();
  const seenIds = useRef(new Set());
  const channelRef = useRef(null);

  const addToast = useCallback((toast) => {
    if (!toast) return;
    setToasts((prev) => {
      // Max 4 toasts at once
      const updated = [toast, ...prev]?.slice(0, 4);
      return updated;
    });
  }, []);

  const dismissToast = useCallback((toastId) => {
    setToasts((prev) => prev?.filter((t) => t?.id !== toastId));
  }, []);

  const handleAction = useCallback((toastId, action) => {
    if (action?.dismiss) return;
    if (action?.path) {
      const state = action?.tab ? { activeTab: action?.tab } : {};
      navigate(action?.path, { state });
    }
    // Mark notification as read
    const toast = toasts?.find((t) => t?.id === toastId);
    if (toast?.notificationId) {
      notificationService?.markAsRead(toast?.notificationId)?.catch(() => {});
    }
  }, [navigate, toasts]);

  useEffect(() => {
    if (!userId) return;

    // Cleanup previous channel
    if (channelRef?.current) {
      channelRef?.current?.unsubscribe();
    }

    const channel = supabase
      ?.channel(`toast_notifications:${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const notification = payload?.new;
          if (!notification?.id) return;
          // Avoid duplicate toasts
          if (seenIds?.current?.has(notification?.id)) return;
          seenIds?.current?.add(notification?.id);

          const toast = buildToastFromNotification(notification);
          if (toast) addToast(toast);

          // Trigger email summaries based on notification type
          const meta = notification?.metadata || {};

          if (notification?.type === 'badge_earned') {
            emailSummaryService?.sendBadgeEmail(userId, {
              name: meta?.badge_name || notification?.title,
              title: meta?.badge_name || notification?.title,
              icon: meta?.badge_icon || '🏅',
              description: notification?.message || '',
              points: meta?.points_earned || 0
            })?.catch(() => {});
          } else if (notification?.type === 'activity_milestone' || notification?.type === 'goal_achievement') {
            emailSummaryService?.sendMilestoneEmail(userId, {
              title: notification?.title,
              description: notification?.message || '',
              icon: meta?.icon || '🎯',
              points: meta?.points_earned || 0,
              category: meta?.category || 'fitness'
            })?.catch(() => {});
          } else if (
            notification?.type === 'duel_invitation' ||
            notification?.type === 'duel_accepted' ||
            notification?.type === 'duel_completed'
          ) {
            const eventTypeMap = {
              duel_invitation: 'invite',
              duel_accepted: 'started',
              duel_completed: 'winner_declared'
            };
            emailSummaryService?.sendDuelSummaryEmail(userId, {
              title: notification?.title,
              eventType: eventTypeMap?.[notification?.type] || 'invite',
              opponentName: meta?.challenger_name || meta?.opponent_name || 'Your opponent',
              isWinner: meta?.is_winner || false,
              metric: meta?.metric || ''
            })?.catch(() => {});
          } else if (notification?.type === 'friend_milestone') {
            emailSummaryService?.sendFriendActivityEmail(userId, {
              friendName: meta?.friend_name || 'Your friend',
              activityType: meta?.activity_type || 'milestone',
              title: notification?.title,
              description: notification?.message || '',
              icon: meta?.icon || '🎯'
            })?.catch(() => {});
          }
        }
      )
      ?.subscribe();

    channelRef.current = channel;

    return () => {
      channel?.unsubscribe();
      channelRef.current = null;
    };
  }, [userId, addToast]);

  return { toasts, dismissToast, handleAction };
};

export default useToastNotifications;
